import React, { useEffect, useRef, useState } from 'react'
import { FaWhatsapp } from 'react-icons/fa'
import { HiCheck, HiOutlineLink, HiOutlineShare } from 'react-icons/hi'
import { productShareLink } from '../brand'
import { discountPercent, formatRupee } from './Price'
import ItemService from '../service/ItemService'

const canNativeShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function'

// sharing a photo file: phones only (desktop share sheets don't offer WhatsApp)
const canSharePhotos = () => canNativeShare() && typeof navigator.canShare === 'function'
    && typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches

const slug = (text) => String(text || 'product').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '').slice(0, 60) || 'product'

// clipboard API when available (https / localhost), otherwise the old textarea copy
const copyText = (text) => {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text)
    return new Promise((resolve, reject) => {
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(area)
        ok ? resolve() : reject(new Error('copy failed'))
    })
}

// "₹499 (MRP ₹599, 17% off)" for the size the shopper has chosen
export const priceLine = (finalPrice, mrp) => {
    if (!finalPrice) return ''
    const off = discountPercent(mrp, finalPrice)
    return formatRupee(finalPrice) + (off > 0 ? ` (MRP ${formatRupee(mrp)}, ${off}% off)` : '')
}

// Share a product: WhatsApp, copy the link, or the phone's own share sheet. The link is the shop's
// https://<shop>/p/{id}, which gives WhatsApp the product photo, name and price for its preview.
const ShareProduct = ({ itemId, title, finalPrice, mrp }) => {
    const [copied, setCopied] = useState(false)
    const [copyFailed, setCopyFailed] = useState(false)
    const [nativeShare] = useState(canNativeShare)
    const [photo, setPhoto] = useState(null) // File ready to share on phones
    const timer = useRef(null)

    useEffect(() => () => clearTimeout(timer.current), [])

    // phones: fetch the product photo ahead of the tap (a share that waits for a download loses the tap's permission),
    // so WhatsApp gets the real photo with the name, price and link as its caption
    useEffect(() => {
        setPhoto(null)
        if (!itemId || !canSharePhotos()) return undefined
        let alive = true
        ItemService.getSharePhoto(itemId).then(res => {
            const file = new File([res.data], slug(title) + '.jpg', { type: 'image/jpeg' })
            if (alive && navigator.canShare({ files: [file] })) setPhoto(file)
        }).catch(() => { /* no photo: the link preview is used */ })
        return () => { alive = false }
    }, [itemId, title])

    const link = productShareLink(itemId)
    const price = priceLine(finalPrice, mrp)
    // the link goes alone on the last line, so WhatsApp builds its preview from it
    const message = ['*' + title + '*', price, link].filter(Boolean).join('\n')

    const flash = (setter) => {
        setter(true)
        clearTimeout(timer.current)
        timer.current = setTimeout(() => { setCopied(false); setCopyFailed(false) }, 2000)
    }

    const copy = () => {
        copyText(link).then(() => { setCopyFailed(false); flash(setCopied) })
            .catch(() => { setCopied(false); flash(setCopyFailed) })
    }

    // with a photo ready: the phone's share sheet sends it (pick WhatsApp there); otherwise the wa.me link opens
    const shareOnWhatsApp = (event) => {
        if (!photo) return
        event.preventDefault()
        navigator.share({ files: [photo], text: message }).catch(error => {
            if (error && error.name === 'AbortError') return // closed by the shopper
            window.open('https://wa.me/?text=' + encodeURIComponent(message), '_blank', 'noopener')
        })
    }

    const share = () => {
        navigator.share({ title, text: [title, price].filter(Boolean).join(' · '), url: link })
            .catch(() => { /* closed by the shopper */ })
    }

    return (
        <div className='mv-share' role='group' aria-labelledby='mv-share-label'>
            <span id='mv-share-label' className='mv-share-label'>Share</span>
            <div className='mv-share-actions'>
                <a className='mv-share-btn is-whatsapp' href={'https://wa.me/?text=' + encodeURIComponent(message)}
                    target='_blank' rel='noopener noreferrer' onClick={shareOnWhatsApp}>
                    <FaWhatsapp aria-hidden='true' /> WhatsApp
                </a>
                <button type='button' className={'mv-share-btn' + (copied ? ' is-done' : '')} onClick={copy}>
                    {copied ? <HiCheck aria-hidden='true' /> : <HiOutlineLink aria-hidden='true' />}
                    {copied ? 'Copied' : copyFailed ? "Couldn't copy" : 'Copy link'}
                </button>
                {nativeShare &&
                    <button type='button' className='mv-share-btn' onClick={share}>
                        <HiOutlineShare aria-hidden='true' /> More
                    </button>}
            </div>
            {photo && <p className='mv-share-hint'>WhatsApp sends the photo with the price and link.</p>}
            <span className='mv-visually-hidden' aria-live='polite'>{copied ? 'Link copied' : ''}</span>
        </div>
    )
}

export default ShareProduct
